import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { Play, Square, RotateCcw, Terminal, Cpu, MemoryStick, Users, Copy, Check, Wrench } from "lucide-react";
import type { ReactNode } from "react";
import toast from "react-hot-toast";
import { serverStore } from "../../stores/server.store";
import { userStore } from "../../stores/user.store";
import { ServerIcon } from "../server/ServerIcon";
import type { Server } from "@/types";
import { cn } from "../../utils/cn";
import { can } from '@/utils/permissions';

const MiniStat = ({ icon, label, value, fill, dim }: { icon: ReactNode; label: string; value: string; fill: number; dim: boolean }) => (
  <div className="min-w-0">
    <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-fg-muted">
      <span className="flex items-center gap-1">{icon}{label}</span>
      <span className={cn("font-mono", dim && "opacity-40")}>{value}</span>
    </div>
    <div className="mt-1 h-1 rounded-full bg-white/[0.06] overflow-hidden">
      <div className={cn("h-full rounded-full transition-all duration-500", dim ? "bg-white/15" : "bg-ember")} style={{ width: `${Math.min(100, Math.max(0, fill * 100))}%` }} />
    </div>
  </div>
);

const IconBtn = ({ children, onClick, title, danger, accent }: { children: ReactNode; onClick: (e: React.MouseEvent) => void; title: string; danger?: boolean; accent?: boolean }) => (
  <button
    title={title}
    aria-label={title}
    onClick={onClick}
    className={cn(
      "w-8 h-8 rounded-lg flex items-center justify-center transition-colors border",
      accent ? "bg-ember/15 border-ember/25 text-ember hover:bg-ember/25"
        : danger ? "border-line text-fg-muted hover:text-red-400 hover:border-red-500/30 hover:bg-red-500/10"
        : "border-line text-fg-muted hover:text-fg hover:bg-white/[0.06]"
    )}
  >
    {children}
  </button>
);

export const ServerCard = observer(({ server }: { server: Server }) => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const online = server.status === "online";
  const busy = ["restarting", "stopping", "starting"].includes(server.status);
  const rd = serverStore.serverRuntimeDetails.get(server.id);
  const cu = userStore.currentUser;
  const canPower = can(cu, "can_power_server");

  const copyId = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(server.id).then(() => {
      setCopied(true);
      toast.success("Server-ID kopiert");
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const cpu = Math.max(0, server.cpu_usage || 0);
  const ram = Math.max(0, server.ram_usage || 0);
  const ramPct = server.ram_limit ? ram / server.ram_limit : 0;

  const open = () => { serverStore.selectedServerId = server.id; navigate(`/server/${server.id}/overview`); };
  const power = (e: React.MouseEvent, status: "online" | "offline" | "restarting") => { e.stopPropagation(); serverStore.powerServer(server.id, status); };

  return (
    <div
      onClick={open}
      className="group cursor-pointer bg-surface border border-line rounded-2xl p-4 hover:border-line-strong transition-colors relative overflow-hidden"
    >
      <div className={cn("absolute top-0 inset-x-0 h-[2px]", online ? "bg-online" : busy ? "bg-warn" : "bg-white/10")} />

      <div className="flex items-start gap-3">
        <div className="relative shrink-0">
          <ServerIcon serverId={server.id} type={server.type} size={40} className="rounded-xl" />
          <span className={cn("absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full border-2 border-surface", online ? "bg-online gm-live-dot" : busy ? "bg-warn" : "bg-fg-muted/50")} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-semibold text-fg truncate leading-tight group-hover:text-ember transition-colors">{server.name}</h3>
          <div className="text-xs text-fg-muted font-mono truncate mt-0.5">
            {server.type}{server.version ? ` ${server.version}` : ""} · :{server.port}
          </div>
          <button
            onClick={copyId}
            title="Server-ID kopieren"
            className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono text-fg-muted/70 hover:text-ember transition-colors max-w-full"
          >
            <span className="truncate">{server.id}</span>
            {copied ? <Check size={10} className="shrink-0 text-online" /> : <Copy size={10} className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-4">
        <MiniStat icon={<Cpu size={11} />} label="CPU" value={`${cpu.toFixed(0)}%`} fill={Math.min(1, cpu / 100)} dim={!online} />
        <MiniStat icon={<MemoryStick size={11} />} label="RAM" value={`${ram.toFixed(1)}/${server.ram_limit}G`} fill={ramPct} dim={!online} />
      </div>

      {/* Laufende Operation (Backup/Install/Archiv/Restore/Selbstheilung) — auf einen Blick */}
      {(() => {
        const act = serverStore.getServerActivity(server.id);
        if (!act) return null;
        const heal = act.kind === "heal";
        return (
          <div className="mt-3">
            <div className={cn("flex items-center justify-between text-[11px] mb-1", heal ? "text-amber-400" : "text-ember")}>
              <span className="truncate mr-2 flex items-center gap-1">{heal && <Wrench size={11} className="animate-pulse shrink-0" />}{heal ? "Selbstheilung…" : act.label}</span>
              {act.progress !== null && <span className="font-mono shrink-0">{act.progress}%</span>}
            </div>
            <div className="h-1 rounded-full bg-white/[0.06] overflow-hidden">
              <div className={cn("h-full transition-all duration-500", heal ? "bg-amber-400" : "bg-ember")} style={{ width: `${act.progress ?? 100}%`, opacity: act.progress === null ? 0.5 : 1 }} />
            </div>
          </div>
        );
      })()}

      <div className="flex items-center justify-between mt-4">
        <div className="text-xs text-fg-muted flex items-center gap-1.5 min-w-0">
          {online && rd?.playerCount ? (
            <><Users size={13} /> <span className="font-mono">{rd.playerCount.current}/{rd.playerCount.max} Spieler</span></>
          ) : (
            <span className="text-fg-muted/70">{online ? "läuft" : busy ? "…" : "gestoppt"}</span>
          )}
        </div>
        {canPower && (
          <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
            {online ? (
              <>
                <IconBtn title="Konsole" onClick={() => { serverStore.selectedServerId = server.id; navigate(`/server/${server.id}/console`); }}><Terminal size={15} /></IconBtn>
                <IconBtn title="Neustart" onClick={(e) => power(e, "restarting")}><RotateCcw size={15} /></IconBtn>
                <IconBtn title="Stoppen" danger onClick={(e) => power(e, "offline")}><Square size={15} /></IconBtn>
              </>
            ) : (
              !busy && <IconBtn title="Starten" accent onClick={(e) => power(e, "online")}><Play size={15} /></IconBtn>
            )}
          </div>
        )}
      </div>
    </div>
  );
});
